import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useFeatureToggle } from '@flopflip/react-redux';
import { getTextField } from '@lender/utils';
import { Box, Button } from '@mui/material';
import storage from 'app/services/storage';
import { TextFieldCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import { object } from 'yup';

import { ConfirmationModalType } from 'constants/confirmationDialog';
import { DEFAULT_DOMAIN_NAME } from 'constants/domains';
import { FeatureFlag } from 'constants/featureFlags';
import { LS_DOMAIN_NAME } from 'constants/localStorage';
import useForm from 'hooks/useForm';
import { getDomainName } from 'utils/domain';
import { getIsNativePlatform } from 'utils/platform';

import { logoutMasterAction } from 'store/auth/actions';
import { selectMasterCompanyId } from 'store/auth/selectors';
import { openConfirmationDialogAction, showMessageAction } from 'store/global/actions';

import { ChangeDomainTestIds } from './ChangeDomain.testIds';

const rootStyles = {
  display: 'flex',
  justifyContent: 'center',
  position: 'absolute',
  top: 73,
  left: '50%',
  marginLeft: '-220px',
  zIndex: 1200,
  width: 440,
  height: 80,
  '& form': {
    display: 'flex',
    padding: '10px',
    justifyContent: 'center',
    backgroundColor: ({ palette }) => palette.background.paper,
    position: 'absolute',
    margin: '12px',
    alignItems: 'stretch',
  },
};

const schema = object().shape({
  domain: getTextField(),
});

const ChangeDomain = () => {
  const dispatch = useDispatch();

  const initialDomain = useMemo(getDomainName, []);

  const isChangeDomainNameAvailable = useFeatureToggle(FeatureFlag.ChangeDomainName);
  const { form, handleChange, setSchema, isFormChanged } = useForm({ domain: initialDomain });

  const masterCompanyId = useSelector(selectMasterCompanyId);

  useEffect(() => {
    setSchema(schema);
  }, [setSchema]);

  const onSubmit = useCallback(
    e => {
      e.preventDefault();
      storage.setItem(LS_DOMAIN_NAME, form.domain);

      dispatch(showMessageAction({ message: 'Successfully changed', variant: 'success' }));

      if (masterCompanyId) {
        dispatch(logoutMasterAction({ isExplicitLogout: true }));
      }

      // To provide time to show Info toast above. Native bundles seed the Company
      // domain at build time, so a hard reload would just re-mount the same domain.
      if (!getIsNativePlatform()) {
        setTimeout(() => window.location.reload(), 0);
      }
    },
    [dispatch, form.domain, masterCompanyId],
  );

  const onReset = useCallback(() => {
    dispatch(
      openConfirmationDialogAction({
        type: ConfirmationModalType.WARNING,
        message: 'Confirm domain reset',
        description: 'Are you sure you want to reset current domain?',
        onAccept: () => {
          storage.setItem(LS_DOMAIN_NAME, DEFAULT_DOMAIN_NAME);

          dispatch(showMessageAction({ message: 'Successfully reseted', variant: 'success' }));

          if (masterCompanyId) {
            dispatch(logoutMasterAction());
          }

          // To provide time to show Info toast above.
          if (!getIsNativePlatform()) {
            setTimeout(() => window.location.reload(), 0);
          }
        },
      }),
    );
  }, [dispatch, masterCompanyId]);

  if (!isChangeDomainNameAvailable) {
    return null;
  }

  return (
    <Box sx={rootStyles}>
      <form noValidate data-ischanged={isFormChanged} onSubmit={onSubmit}>
        <TextFieldCDS
          autoFocus
          required
          className="min-w-256"
          dataTestId={ChangeDomainTestIds.DOMAIN_INPUT}
          id="domain"
          label="Domain"
          name="domain"
          size="small"
          type="text"
          value={form.domain}
          variant="outlined"
          onChange={handleChange}
        />
        <Box className="ml-8 flex items-end gap-8">
          <Button
            data-testid={ChangeDomainTestIds.SAVE_BUTTON}
            disabled={!isFormChanged || !form.domain}
            type="submit"
            variant="contained"
          >
            Save
          </Button>

          <Button
            data-testid={ChangeDomainTestIds.RESET_BUTTON}
            disabled={DEFAULT_DOMAIN_NAME === form.domain}
            type="button"
            variant="contained"
            onClick={onReset}
          >
            Reset
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default ChangeDomain;
